import React from 'react';
import type { ConnectorStatus } from '@/types/connectors';

export interface AggregateHealthBarProps {
  total: number;
  counts: Partial<Record<ConnectorStatus, number>>;
  onFilterByStatus: (status: ConnectorStatus | null) => void;
  /** Currently-applied status filter(s); used to highlight the active segment. */
  activeStatuses?: ConnectorStatus[];
}

interface Segment {
  key: 'all' | ConnectorStatus;
  label: string;
  count: number;
  dotClass?: string;
  emphasis?: boolean;
}

export const AggregateHealthBar: React.FC<AggregateHealthBarProps> = ({
  total,
  counts,
  onFilterByStatus,
  activeStatuses = [],
}) => {
  const segments: Segment[] = [
    { key: 'all', label: 'Total connectors', count: total },
    { key: 'connected', label: 'Healthy', count: counts.connected ?? 0, dotClass: 'bg-success' },
    { key: 'running', label: 'Running', count: counts.running ?? 0, dotClass: 'bg-warning' },
    { key: 'error', label: 'Errors', count: counts.error ?? 0, dotClass: 'bg-destructive', emphasis: true },
    { key: 'disabled', label: 'Disabled', count: counts.disabled ?? 0, dotClass: 'bg-muted-foreground' },
  ];

  const isActive = (key: Segment['key']) =>
    key === 'all'
      ? activeStatuses.length === 0
      : activeStatuses.length === 1 && activeStatuses[0] === key;

  return (
    <div className="flex items-stretch rounded-lg border bg-card overflow-hidden w-full">
      {segments.map((seg, i) => {
        const active = isActive(seg.key);
        return (
          <button
            key={seg.key}
            type="button"
            aria-pressed={active}
            onClick={() => onFilterByStatus(seg.key === 'all' ? null : (seg.key as ConnectorStatus))}
            className={[
              'flex-1 min-w-0 flex flex-col gap-0.5 px-4 py-3 text-left transition-colors',
              i < segments.length - 1 ? 'border-r' : '',
              'hover:bg-accent',
              active ? 'bg-primary/10 shadow-[inset_0_-2px_0_0_var(--color-primary)]' : '',
            ].join(' ')}
          >
            <span
              className={`text-[22px] font-bold leading-none tracking-tight ${
                seg.emphasis ? 'text-destructive' : ''
              }`}
            >
              {seg.count}
            </span>
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              {seg.dotClass && <span className={`h-2 w-2 rounded-full ${seg.dotClass}`} />}
              {seg.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
